// 顾客端登录接口适配层（原 apps/customer/src/auth/api.ts 的 Vue2/JS 版本）。
import {
  AuthError,
  httpTransport,
  isRecord as record,
  validateOrigin,
} from './http'

// 登录流程按原实现从本模块取 AuthError，这里显式再导出，避免绑定缺失。
export { AuthError }

const messages = {
  NOT_CONFIGURED: '登录服务尚未配置，暂时无法登录。',
  INVALID_CONFIG: '登录服务地址配置无效，请联系管理员。',
  INVALID_REQUEST: '请检查手机号和验证码格式。',
  PHONE_ALREADY_BOUND: '当前账号已绑定手机号，请刷新个人中心。',
  PHONE_IN_USE: '这个手机号已属于其他使用中的账号，请使用手机号登录或联系客服核对。',
  PHONE_BIND_REQUIRES_REVIEW: '充值余额关联需要人工核对，请联系客服。',
  PHONE_BIND_RETRY: '手机号关联状态刚刚更新，请稍后重试绑定。',
  INVALID_VERIFICATION_CODE: '验证码无效或已过期，请检查后重试或重新获取。',
  UNAUTHORIZED: '登录已失效，请重新登录。',
  FORBIDDEN: '当前账户暂时无法使用此服务。',
  RATE_LIMITED: '操作过于频繁，请稍后重试。',
  AUTH_PROVIDER_UNAVAILABLE: '登录服务暂未开放，请稍后再试。',
  SMS_DAILY_LIMIT: '该手机号今日验证码次数已达上限，请明天再试，或使用微信登录。',
  SMS_DELIVERY_FAILED: '验证码暂时发送失败，请稍后重试或使用微信登录。',
  AUTH_SERVICE_UNAVAILABLE: '登录服务暂不可用，请稍后重试。',
  NETWORK: '网络连接失败，请检查网络后重试。',
  TIMEOUT: '请求超时，请稍后重试。',
  INVALID_RESPONSE: '登录服务响应异常，请稍后重试。',
  CANCELLED: '此次操作已取消，请重新操作。',
  CHALLENGE_REQUIRED: '请先获取有效验证码。',
  DISABLED: '短信登录暂未开放。',
  // 微信回调（/api/wx/callback）失败时后端 302 带回来的 login_error 错误码。
  QRSCENE_NOT_FOUND: '二维码已失效，请点击「刷新二维码」重新扫码。',
  QRSCENE_NOT_PENDING: '二维码状态异常，请点击「刷新二维码」重新扫码。',
  QRSCENE_EXPIRED: '二维码已过期，请点击「刷新二维码」重新扫码。',
  WECHAT_AUTH_FAILED: '微信登录暂未完成，请稍后重新登录。',
  WECHAT_CONFIG_ERROR: '微信登录配置异常，请联系管理员检查 AppID 和 AppSecret。',
  WECHAT_CODE_EXPIRED: '此次微信授权已过期或已使用，请重新登录。',
  WECHAT_INVALID_RESPONSE: '微信登录服务返回异常，请稍后重试。',
  WECHAT_CALLBACK_NETWORK: '连接微信服务失败，请稍后重试。',
  WECHAT_BROWSER_REQUIRED: '请在微信中打开本网站，再点击微信登录。',
  WECHAT_LOGIN_EXPIRED: '此次微信授权已失效，请重新点击微信登录。',
  WECHAT_AUTH_CANCELLED: '此次微信授权已取消，可以重新登录。',
  WECHAT_UNIONID_REQUIRED: '微信登录配置尚未完成，请使用验证码登录或联系客服。',
  WECHAT_ACCOUNT_CONFLICT: '微信账号关联需要核实，请联系客服。',
  WECHAT_CALLBACK_FAILED: '微信登录回调异常，请稍后重试。',
}

export function authMessage(error) {
  return error instanceof AuthError
    ? messages[error.code] || messages.AUTH_SERVICE_UNAVAILABLE
    : messages.AUTH_SERVICE_UNAVAILABLE
}

/** 微信回调失败错误码 → 中文提示；未知错误码回退到通用文案。 */
export function wechatCallbackMessage(code) {
  if (!code) return ''
  return messages[code] || messages.WECHAT_CALLBACK_FAILED
}

export function capabilityReason(capability) {
  if (!capability) return '尚未取得服务能力'
  if (capability.reason === 'PROVIDER_NOT_CONFIGURED') return '服务尚未配置'
  return capability.enabled ? '当前阶段尚未接入此登录方式' : '服务暂未开放'
}

export function normalizePhone(input) {
  const value = String(input || '').trim()
  if (!/^(?:\+86)?1[3-9]\d{9}$/.test(value)) throw new AuthError('INVALID_REQUEST')
  return value.startsWith('+86') ? value : `+86${value}`
}

const id = (x) =>
  typeof x === 'string' && x.length > 0 && x.length <= 512 && !/[\s\x00-\x1f\x7f]/.test(x)
const utc = (x) =>
  typeof x === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/.test(x) &&
  Number.isFinite(Date.parse(x))

function invalid() {
  throw new AuthError('INVALID_RESPONSE')
}

function user(x) {
  if (
    !record(x) ||
    !id(x.id) ||
    !(x.phoneMasked === null || (typeof x.phoneMasked === 'string' && /^1[3-9]\d\*{4}\d{4}$/.test(x.phoneMasked)))
  )
    return invalid()
  return { id: x.id, phoneMasked: x.phoneMasked }
}

export function parseMe(x) {
  if (!record(x) || !utc(x.expiresAt)) return invalid()
  return { user: user(x.user), expiresAt: x.expiresAt }
}

export function parseSession(x) {
  if (
    !record(x) ||
    !id(x.accessToken) ||
    !/^[A-Za-z0-9._~+/-]+=*$/.test(x.accessToken) ||
    x.tokenType !== 'Bearer'
  )
    return invalid()
  return Object.assign({}, parseMe(x), { accessToken: x.accessToken, tokenType: 'Bearer' })
}

export function parseChallenge(x) {
  if (
    !record(x) ||
    !id(x.challengeId) ||
    !utc(x.expiresAt) ||
    !Number.isSafeInteger(x.resendAfterSeconds) ||
    x.resendAfterSeconds < 0
  )
    return invalid()
  return {
    challengeId: x.challengeId,
    expiresAt: x.expiresAt,
    resendAfterSeconds: x.resendAfterSeconds,
  }
}

export function parseCapabilities(x) {
  function cap(v) {
    if (
      !record(v) ||
      typeof v.enabled !== 'boolean' ||
      (v.enabled ? v.reason !== null : typeof v.reason !== 'string' || !/^[A-Z_]{1,80}$/.test(v.reason))
    )
      return invalid()
    return { enabled: v.enabled, reason: v.reason }
  }
  if (!record(x)) return invalid()
  return { sms: cap(x.sms), wechatWeb: cap(x.wechatWeb), wechatMiniProgram: cap(x.wechatMiniProgram), wechatMobile: x.wechatMobile ? cap(x.wechatMobile) : { enabled: false, reason: 'PROVIDER_NOT_CONFIGURED' } }
}

const wechatAppId = (x) => typeof x === 'string' && /^wx[0-9a-zA-Z]{16}$/.test(x)
const httpsUrl = (x) =>
  typeof x === 'string' && x.length <= 2048 && /^https:\/\/[^\s\\\x00-\x1f\x7f]+$/.test(x)

// 二维码改由微信官方 wxLogin.js 渲染，所以这里只要 appId / redirectUri / state(qrsceneId)。
// 后端不再下发 qrconnect URL —— 自己拿它生成二维码会导致扫码后手机上再出现一个二维码（套娃）。
export function parseWechatQrscene(x) {
  if (
    !record(x) ||
    !id(x.qrsceneId) ||
    !wechatAppId(x.appId) ||
    !httpsUrl(x.redirectUri) ||
    !utc(x.expiresAt)
  )
    return invalid()
  return { qrsceneId: x.qrsceneId, appId: x.appId, redirectUri: x.redirectUri, expiresAt: x.expiresAt }
}

export function parseWechatQrscenePoll(x) {
  if (
    !record(x) ||
    typeof x.status !== 'string' ||
    !['PENDING', 'CONFIRMED', 'EXPIRED', 'CONSUMED'].includes(x.status)
  )
    return invalid()
  return { status: x.status }
}

export function createAuthApi(base, development = false, transport = httpTransport) {
  async function request(path, method, expected, data, token) {
    const origin = validateOrigin(base, development)
    let response
    try {
      response = await transport({
        url: origin + '/v1/auth/' + path,
        method,
        data,
        headers: Object.assign(
          { 'Content-Type': 'application/json' },
          token ? { Authorization: `Bearer ${token}` } : {},
        ),
      })
    } catch (error) {
      throw error instanceof AuthError ? error : new AuthError('NETWORK')
    }
    if (response.status !== expected) {
      const allowed = {
        400: ['INVALID_REQUEST', 'WECHAT_BROWSER_REQUIRED', 'INVALID_VERIFICATION_CODE'],
        401: ['INVALID_VERIFICATION_CODE', 'UNAUTHORIZED'],
        403: ['FORBIDDEN'],
        409: ['PHONE_ALREADY_BOUND', 'PHONE_IN_USE', 'PHONE_BIND_RETRY', 'PHONE_BIND_REQUIRES_REVIEW'],
        429: ['RATE_LIMITED', 'SMS_DAILY_LIMIT'],
        503: ['AUTH_PROVIDER_UNAVAILABLE', 'AUTH_SERVICE_UNAVAILABLE', 'SMS_DELIVERY_FAILED'],
      }
      const code =
        record(response.data) &&
        typeof response.data.code === 'string' &&
        (allowed[response.status] || []).includes(response.data.code)
          ? response.data.code
          : response.status === 401
            ? 'UNAUTHORIZED'
            : 'AUTH_SERVICE_UNAVAILABLE'
      const retry = Object.entries(response.headers).find(([key]) => key.toLowerCase() === 'retry-after')
      const seconds =
        retry && typeof retry[1] === 'string' && /^\d+$/.test(retry[1])
          ? Number(retry[1])
          : 0
      throw new AuthError(code, response.status, response.status === 429 ? seconds : 0)
    }
    return response.data
  }

  return {
    phoneChallenge: async (token, phone) => parseChallenge(await request('phone/challenges', 'POST', 201, { phone: normalizePhone(phone) }, token)),
    bindPhone: async (token, challengeId, code) => { if (!id(challengeId) || !/^\d{6}$/.test(code)) throw new AuthError('INVALID_REQUEST'); return parseMe(await request('phone/bind', 'POST', 200, { challengeId, code }, token)) },
    restore: async () => parseSession(await request('browser-session', 'POST', 200)),
    capabilities: async () => parseCapabilities(await request('capabilities', 'GET', 200)),
    challenge: async (phone) =>
      parseChallenge(await request('sms/challenges', 'POST', 201, { phone: normalizePhone(phone) })),
    login: async (challengeId, code) => {
      if (!id(challengeId) || !/^\d{6}$/.test(code)) throw new AuthError('INVALID_REQUEST')
      return parseSession(await request('sms/login', 'POST', 200, { challengeId, code }))
    },
    me: async (token) => parseMe(await request('me', 'GET', 200, undefined, token)),
    logout: async (token) => {
      await request('logout', 'POST', 204, undefined, token)
    },
    // ---------- 微信扫码登录（深度 2：真接口接通） ----------
    // 后端契约（已实现，cn.detian.platform.auth.wechat.WechatOAuthController）：
    //   POST /v1/auth/wechat/qrscene                → { qrsceneId, appId, redirectUri, expiresAt }
    //   GET  /v1/auth/wechat/qrscene/{id}          → { status: 'PENDING'|'CONFIRMED'|'EXPIRED'|'CONSUMED' }
    //   POST /v1/auth/wechat/qrscene/{id}/confirm  → { accessToken, tokenType, expiresAt, user }
    // 前端拿到 CONFIRMED 后再 confirm 一次性取 token，避免轮询响应体暴露 access token。
    wechatMobileStart: async (returnTo) => {
      const result = await request('wechat/mobile/start', 'POST', 200, { returnTo })
      if (!record(result) || typeof result.authorizeUrl !== 'string') return invalid()
      return result.authorizeUrl
    },
    wechatQrscene: async () => {
      return parseWechatQrscene(await request('wechat/qrscene', 'POST', 200))
    },
    wechatPoll: async (qrsceneId) => {
      if (!id(qrsceneId)) throw new AuthError('INVALID_REQUEST')
      return parseWechatQrscenePoll(await request('wechat/qrscene/' + qrsceneId, 'GET', 200))
    },
    wechatConfirm: async (qrsceneId) => {
      if (!id(qrsceneId)) throw new AuthError('INVALID_REQUEST')
      return parseSession(await request('wechat/qrscene/' + qrsceneId + '/confirm', 'POST', 200))
    },
  }
}
